import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Route,
  Sparkles,
  GraduationCap,
  Video,
  CreditCard,
  ArrowRight,
  User,
} from "lucide-react";
import useAuth from "../../hooks/useAuth";
import useRetryFetch from "../../hooks/useRetryFetch";
import { config } from "../../Environment";

/**
 * ClaryNext Studio home — the coach/expert shell of /home (userType=expert).
 * Studio-first: create/continue journeys, reach clients and meetings, see the
 * plan upsell. The seeker cockpit stays one click away via onSwitchToPersonal.
 */
const StudioHome = ({ onSwitchToPersonal }: { onSwitchToPersonal: () => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { auth, user } = useAuth();
  const retryFetch = useRetryFetch();

  const firstName =
    user?.firstName || user?.displayName?.split(" ")[0] || auth?.email?.split("@")[0] || "";

  const [journeys, setJourneys] = useState<any[]>([]);
  const [journeysLoaded, setJourneysLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await retryFetch(`${config.API_BASE}/api/pwf?isTemplate=true`);
        if (!response.ok) return;
        const data = await response.json();
        const list = data?.pwfs ?? data?.list ?? (Array.isArray(data) ? data : []);
        if (!cancelled) setJourneys(Array.isArray(list) ? list : []);
      } catch {
        // list stays empty
      } finally {
        if (!cancelled) setJourneysLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasJourneys = journeys.length > 0;

  // The Clients hub (/mentoring) reads its tab from localStorage on mount
  const openMentoringTab = (tab: string) => {
    localStorage.setItem("mentoring_active_tab", tab);
    navigate("/mentoring");
  };

  const trialDaysLeft = (() => {
    if (!user?.studioTrialStartedAt) return null;
    const started = new Date(user.studioTrialStartedAt).getTime();
    if (Number.isNaN(started)) return null;
    const left = 30 - Math.floor((Date.now() - started) / 86400000);
    return left > 0 && left <= 30 ? left : null;
  })();

  return (
    <div className="flex-1 overflow-y-auto scroll-smooth">
      <div className="max-w-4xl mx-auto px-6 md:px-8 py-8 space-y-8">
        {/* Hero */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-primary">
              <Sparkles className="h-3.5 w-3.5" />
              {t("studio_home_eyebrow", "ClaryNext Studio")}
            </span>
            <button
              onClick={onSwitchToPersonal}
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white/70 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <User className="h-3.5 w-3.5" />
              {t("studio_home_personal", "My personal home")}
            </button>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
            {firstName
              ? t("studio_home_welcome", "Welcome back, {{name}}", { name: firstName })
              : t("studio_home_welcome_anon", "Welcome back")}
          </h1>
          <p className="text-muted-foreground max-w-xl">
            {t(
              "studio_home_subtitle",
              "Build transformation journeys your clients actually finish — external work and identity growth, woven together."
            )}
          </p>
        </div>

        {/* Primary action */}
        <button
          onClick={() => navigate("/pwf")}
          className="group w-full rounded-2xl border border-primary/25 bg-primary/5 p-6 text-start transition-all hover:border-primary/50 hover:bg-primary/10"
        >
          <div className="flex items-center gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Route className="h-6 w-6" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-base font-semibold text-foreground">
                {hasJourneys
                  ? t("studio_home_open_studio", "Open your Studio")
                  : t("studio_home_first_journey", "Create your first journey")}
              </span>
              <span className="block text-sm text-muted-foreground mt-0.5">
                {hasJourneys
                  ? t("studio_home_journey_count", "{{count}} journeys in your studio", {
                      count: journeys.length,
                    })
                  : t(
                      "studio_home_first_journey_sub",
                      "Answer a few questions and the AI wizard drafts the full arc — phases, habits, identity."
                    )}
              </span>
            </span>
            <ArrowRight className="h-5 w-5 shrink-0 text-primary transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
          </div>
        </button>

        {/* Secondary actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <StudioCard
            icon={GraduationCap}
            title={t("studio_home_clients", "Your clients")}
            subtitle={t("studio_home_clients_sub", "Mentees, segments, and progress")}
            onClick={() => openMentoringTab("mentees")}
          />
          <StudioCard
            icon={Video}
            title={t("studio_home_meetings", "Meetings")}
            subtitle={t("studio_home_meetings_sub", "Sessions with built-in video")}
            onClick={() => openMentoringTab("meetings")}
          />
          <StudioCard
            icon={CreditCard}
            title={t("studio_home_plans", "Plans & trial")}
            subtitle={
              trialDaysLeft !== null
                ? t("studio_home_trial_left", "Trial — {{count}} days left", { count: trialDaysLeft })
                : t("studio_home_plans_sub", "See what each Studio plan includes")
            }
            highlight={trialDaysLeft !== null}
            onClick={() => navigate("/plans")}
          />
        </div>

        {/* Recent journeys */}
        {journeysLoaded && hasJourneys && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {t("studio_home_recent", "Your journeys")}
            </h2>
            <div className="space-y-2">
              {journeys.slice(0, 5).map((j: any) => (
                <button
                  key={j._id}
                  onClick={() => navigate(`/pwf?id=${j._id}`)}
                  className="flex w-full items-center gap-3 rounded-xl border border-gray-200/70 bg-white/70 px-4 py-3 text-start transition-colors hover:border-primary/40"
                >
                  <Route className="h-4 w-4 shrink-0 text-primary/70" />
                  <span className="flex-1 truncate text-sm font-medium text-foreground">
                    {j.title || j.name || t("studio_home_untitled", "Untitled journey")}
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground/40 rtl:rotate-180" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StudioCard = ({
  icon: Icon,
  title,
  subtitle,
  onClick,
  highlight = false,
}: {
  icon: typeof Route;
  title: string;
  subtitle: string;
  onClick: () => void;
  highlight?: boolean;
}) => (
  <button
    onClick={onClick}
    className={
      highlight
        ? "group rounded-xl border border-amber-300/70 bg-amber-50/60 p-4 text-start transition-all hover:border-amber-400 hover:shadow-sm"
        : "group rounded-xl border border-gray-200/70 bg-white/70 p-4 text-start transition-all hover:border-primary/40 hover:shadow-sm"
    }
  >
    <Icon className={highlight ? "h-5 w-5 text-amber-600 mb-2.5" : "h-5 w-5 text-primary/80 mb-2.5"} />
    <span className="block text-sm font-semibold text-foreground">{title}</span>
    <span className="block text-xs text-muted-foreground mt-0.5 leading-relaxed">{subtitle}</span>
  </button>
);

export default StudioHome;
